"use client";

import { m, useInView } from "framer-motion";
import React, { useRef } from "react";
import SectionHeader from "../sectionHeader";

interface ChannelItem {
  channel: string;
  description: string;
}

interface Props {
  data?: {
    title?: string;
    headline?: string;
    text?: string;
    columnLabels?: [string, string];
    items: ChannelItem[];
  };
}

const itemVariants = {
  initial: { opacity: 0, y: 32 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      type: "spring",
    },
  },
};

export default function ChannelsSection({ data }: Props) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <m.section
      ref={ref}
      className="section"
      transition={{ staggerChildren: 0.05, delayChildren: 0.2 }}
      initial="initial"
      animate={isInView ? "animate" : "initial"}
    >
      <div className="container mx-auto flex flex-col gap-12">
        <SectionHeader title={data?.title} headline={data?.headline} text={data?.text} />

        <div className="grid grid-cols-[auto_1fr] md:grid-cols-[1fr_2fr] gap-x-4 w-full">
          {/* Column headers */}
          {data?.columnLabels && (
            <>
              <span className="font-mono text-sm uppercase tracking-wider pb-4">{data.columnLabels[0]}</span>
              <span className="font-mono text-sm uppercase tracking-wider pb-4">{data.columnLabels[1]}</span>
            </>
          )}

          {/* Rows */}
          {data?.items?.map((item, index) => (
            <React.Fragment key={index}>
              <m.div variants={itemVariants} className="col-span-2">
                <m.div
                  className="w-full h-[3px] bg-primary"
                  initial={{ width: "0%" }}
                  animate={{ width: isInView ? "100%" : "0%" }}
                  transition={{
                    duration: 1,
                    delay: 0.1 * index + 0.2,
                    ease: [0.83, 0, 0.17, 1],
                  }}
                />
              </m.div>
              <m.span variants={itemVariants} className="h3 uppercase py-5">{item.channel}</m.span>
              <m.span variants={itemVariants} className="font-mono uppercase text-sm leading-relaxed py-5 text-balance">{item.description}</m.span>
            </React.Fragment>
          ))}

          {/* Bottom line */}
          <m.div
            className="col-span-2 w-full h-[3px] bg-primary"
            initial={{ width: "0%" }}
            animate={{ width: isInView ? "100%" : "0%" }}
            transition={{
              duration: 1,
              delay: 0.1 * (data?.items?.length ?? 0) + 0.2,
              ease: [0.83, 0, 0.17, 1],
            }}
          />
        </div>
      </div>
    </m.section>
  );
}
